import { useState, useEffect, useRef } from 'react';
import { Box, useTheme } from '@mui/material';
import MessageHistory from './MessageHistory.jsx'; // Updated import

function useInterval(callback, delay) {
  const savedCallback = useRef();

  // Remember the latest callback
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // Set up the interval
  useEffect(() => {
    if (delay !== null) {
      const id = setInterval(() => savedCallback.current(), delay);
      return () => clearInterval(id);
    }
  }, [delay]);
}


function Demo() {
    const [messages, setMessages] = useState([]); // Demo message list
    const [currentIndex, setCurrentIndex] = useState(0); // Track which message to play next
    const theme = useTheme(); // Get theme for styling
  
    // Pre-written demo messages
    const demoMessages = [
      { role: 'user', content: 'Quick overview of today?' },
      { role: 'assistant', content: 'Sales solid at $4,080; NSPT at $16.20. Salad orders up 18%, likely due to warmer weather. All but 2 of 36 daily tasks completed; freshness checks missed on afternoon bread.' },
      { role: 'user', content: 'Any proactive advice?' },
      { role: 'assistant', content: 'Rain forecasted tomorrow—historically boosts delivery orders by ~15%. Schedule extra delivery driver from 11 AM–2 PM?' },
      { role: 'user', content: 'Good idea. Employee highlights?' },
      { role: 'assistant', content: 'Taylor lifted NSPT ~12% by suggesting meal upgrades. Alex struggled with sandwich consistency (-9% quality feedback). Recommend pairing him with Taylor tomorrow.' },
      { role: 'user', content: 'Do it. Any operational concerns?' },
      { role: 'assistant', content: 'Panini grill slowed prep by 10 seconds per order due to uneven heating—needs deep clean tonight. Alert maintenance?' },
      { role: 'user', content: 'Please. Bread texture was off—reason?' },
      { role: 'assistant', content: 'Dough humidity absorption increased by 7%, creating denser bread. Recommend reducing dough water by 5% and extending proofing by 15 mins tomorrow. Notify team?' },
      { role: 'user', content: 'Yes. Labor variances today?' },
      { role: 'assistant', content: 'Labor ran at $68 SPLH vs. target $75—Anthony called out, and Jamie was 20 mins late. Tomorrow, consider slight over-scheduling to offset potential variances?' },
      { role: 'user', content: 'Agreed. Boost afternoon sales suggestions?' },
      { role: 'assistant', content: 'On rainy days, coffee-and-cookie combos typically lift afternoon sales ~14%. Launch promo tomorrow?' },
      { role: 'user', content: 'Yes, prep that. Equipment checks needed?' },
      { role: 'assistant', content: 'Coffee machine nearing maintenance limit. Schedule preventive service tonight to avoid ~20-min downtime?' },
      { role: 'user', content: 'Please. Employee training ideas?' },
      { role: 'assistant', content: 'Jamie trails peers in upselling (NSPT 11% below average). Short coaching session with Taylor tomorrow morning?' },
      { role: 'user', content: 'Yes, great idea. Anything else?' },
      { role: 'assistant', content: 'All set! I\'ll monitor changes tomorrow and keep you updated.' },
      { role: 'user', content: 'How\'s this month\'s performance vs last year?' },
      { role: 'assistant', content: 'Sales are up 9% this month vs. last year—partly driven by higher NSPT ($16.20 vs. $15.10 last year). Keeping sandwich consistency high will sustain this growth.' },
      { role: 'user', content: 'Great. How close are we to our incentive?' },
      { role: 'assistant', content: 'Currently at $82,400—need $100K monthly for next incentive tier (bonus for team). Daily target now ~$4,400. Encourage combo upgrades tomorrow?' },
      { role: 'user', content: 'Yes. Could you translate a team incentive reminder to Spanish?' },
      { role: 'assistant', content: 'Sure: "¡Estamos cerca del incentivo mensual! Lleguemos juntos a $100K y ganemos todos. ¡Ofrezcan esos combos!" Share this with the team?' },
      { role: 'user', content: 'Absolutely. How\'s our inventory looking?' },
      { role: 'assistant', content: 'Good overall, but running low on turkey slices and romaine lettuce—both up ~20% in orders this week. Recommend ordering extra tomorrow?' },
      { role: 'user', content: 'Yes, order extra. Did we have any refunds today?' },
      { role: 'assistant', content: 'Two refunds today—both related to delayed sandwiches from panini grill issue earlier. Addressed by offering free cookie vouchers. Grill maintenance tonight will resolve this.' },
      { role: 'user', content: 'Good. Any notable customer feedback?' },
      { role: 'assistant', content: 'Multiple positive reviews about Taylor\'s customer service today (+15% mentions vs. average). Suggest a quick shout-out tomorrow morning?' },
      { role: 'user', content: 'Agreed. What\'s tomorrow\'s optimal labor schedule?' },
      { role: 'assistant', content: 'Schedule at ~$80 SPLH tomorrow to cushion potential call-outs (forecast rain could impact employee arrival times). Want me to adjust tomorrow\'s schedule accordingly?' },
      { role: 'user', content: 'Yes, please do. What\'s our top-selling item recently?' },
      { role: 'assistant', content: 'The Chipotle Turkey Club—sales up 17% this month. Highlighting it as "Team Favorite" could further boost orders. Ready to add signage tomorrow?' },
      { role: 'user', content: 'Great idea, set that up. Any compliance or safety concerns?' },
      { role: 'assistant', content: 'No major issues. One minor slip reported near prep station today. Recommend quick anti-slip mat check and brief reminder during tomorrow\'s pre-shift meeting?' },
      { role: 'user', content: 'Perfect, schedule it. Anything else we should cover?' },
      { role: 'assistant', content: 'All set for now! I\'ll keep tracking everything closely and update you with any insights.' },
    ];
  
    // Use interval to simulate a chat by sequentially adding messages
    useInterval(() => {
      if (currentIndex < demoMessages.length) {
        // Transform the demo message into the format needed by MessageList
        const newMessage = {
          user: demoMessages[currentIndex].role === 'user' ? 'user' : 'system',
          msg: demoMessages[currentIndex].content,
          timestamp: new Date().toISOString(),
        };
  
        setMessages((prev) => [...prev, newMessage]);
        setCurrentIndex((prev) => prev + 1);
      }
    }, 3000); // Add a message every 3 seconds
  
    return (
      <Box display="flex" flexDirection="column" height="100%" overflow="hidden">

        {/* Message History */}
        <MessageHistory messages={messages} showMessageActions={false} />

        {/* Demo Disclaimer */}
        <Box
          sx={{
            position: 'sticky',
            bottom: 0,
            padding: 2,
            borderTop: `1px solid ${theme.palette.border.subtle}`,
            textAlign: 'center',
            fontSize: '0.875rem',
            color: 'theme.palette.text.muted',
            backgroundColor: theme.palette.background.paper,
          }}
        >
          <em>This is a demo version of the chat. Interaction is disabled.</em>
        </Box>
      </Box>
    );
  }


export default Demo;
